import { useRef, useState } from 'react';
import { parseImport, type ParseResult } from '@/import/parse';
import { applyBlock, planBlock, planBlocks, type ImportDecision, type ImportPlan } from '@/import/apply';
import { CLASS_CHAT_PROMPT } from '@/constants/instructions';
import { useAppState } from '@/ui/AppState';
import { navigate } from '@/ui/router';
import { copyText } from '@/ui/clipboard';
import { Banner, Dialog, IssueList } from '@/ui/components/bits';

type Phase = 'input' | 'preview' | 'done';

interface AppliedSummary {
  sessionId: string;
  decision: ImportDecision;
  items: number;
  removed: number;
}

export function AddClassScreen() {
  const { reload, toast } = useAppState();
  const fileInput = useRef<HTMLInputElement>(null);

  const [text, setText] = useState('');
  const [phase, setPhase] = useState<Phase>('input');
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [plans, setPlans] = useState<ImportPlan[]>([]);
  const [conflict, setConflict] = useState<{ plan: ImportPlan; queue: ImportPlan[] } | null>(null);
  const [applied, setApplied] = useState<AppliedSummary[]>([]);
  const [busy, setBusy] = useState(false);

  async function check(source = text) {
    const result = parseImport(source);
    setParsed(result);
    if (result.blocks.length === 0) {
      setPlans([]);
      setPhase('input');
      return;
    }
    setPlans(await planBlocks(result.blocks));
    setPhase('preview');
  }

  /**
   * Several files at once are concatenated and checked as one paste. The parser
   * already finds every block in a body of text, and the apply queue re-plans
   * each block against the database as it goes — so part 2 of a session may sit
   * in a different file from part 1. Files are read in the order chosen.
   */
  async function onPickFiles(files: File[]) {
    const bodies = await Promise.all(files.map((f) => f.text()));
    setText(bodies.join('\n\n'));
    await check(bodies.join('\n\n'));
  }

  /**
   * The blocks that ship with the app, served from its own origin by
   * scripts/copy-seed.mjs. This is the ordinary importer reading ordinary
   * import blocks — the only difference is where the text came from — so a new
   * device needs neither the files nor a working sync to get its content.
   */
  async function loadBundled() {
    setBusy(true);
    try {
      const index = await fetch('/seed/index.json');
      if (!index.ok) throw new Error('This build has no bundled sessions.');
      const { files } = (await index.json()) as { files: string[] };
      const bodies: string[] = [];
      for (const name of files) {
        const response = await fetch(`/seed/${name}`);
        if (!response.ok) throw new Error(`Could not read ${name}.`);
        bodies.push(await response.text());
      }
      setText(bodies.join('\n\n'));
      await check(bodies.join('\n\n'));
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function runQueue(queue: ImportPlan[], soFar: AppliedSummary[]) {
    const remaining = [...queue];
    const done = [...soFar];

    while (remaining.length > 0) {
      const queued = remaining.shift()!;
      // Re-plan against the database as it stands now: an earlier block in this
      // same run may have created the session this one appends to.
      const plan = await planBlock(queued.block);
      if (plan.blocker) {
        toast(plan.blocker);
        continue;
      }
      if (plan.action === 'conflict') {
        setApplied(done);
        setConflict({ plan, queue: remaining });
        return;
      }
      const result = await applyBlock(plan.block, plan.action === 'append' ? 'append' : 'create');
      done.push({
        sessionId: result.sessionId,
        decision: result.decision,
        items: result.itemsWritten,
        removed: result.itemsRemoved,
      });
    }

    setApplied(done);
    setConflict(null);
    setPhase('done');
    await reload();
  }

  async function confirm() {
    setBusy(true);
    try {
      await runQueue(plans, []);
    } finally {
      setBusy(false);
    }
  }

  async function resolveConflict(decision: ImportDecision) {
    if (!conflict) return;
    setBusy(true);
    try {
      const result = await applyBlock(conflict.plan.block, decision);
      const next = [
        ...applied,
        {
          sessionId: result.sessionId,
          decision: result.decision,
          items: result.itemsWritten,
          removed: result.itemsRemoved,
        },
      ];
      const queue = conflict.queue;
      setConflict(null);
      await runQueue(queue, next);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setText('');
    setParsed(null);
    setPlans([]);
    setApplied([]);
    setPhase('input');
  }

  const blockers = plans.filter((p) => p.blocker);

  return (
    <div className="stack">
      <h1>Add class</h1>

      <div className="card stack">
        <p className="muted" style={{ margin: 0 }}>
          Copy the instructions into a Claude chat, add your notes, your professor&rsquo;s slides, a
          recitation sheet or a textbook section, then paste the import block it replies with. On a
          device with nothing on it yet, Load bundled sessions below brings in everything that ships
          with the app.
        </p>
        <div className="row">
          <button
            className="btn"
            onClick={async () => {
              const ok = await copyText(CLASS_CHAT_PROMPT);
              toast(ok ? 'Instructions copied.' : 'Could not reach the clipboard.');
            }}
          >
            Copy instructions
          </button>
        </div>
      </div>

      {phase !== 'done' ? (
        <>
          <div>
            <label className="label" htmlFor="import-text">
              Import block
            </label>
            <textarea
              id="import-text"
              className="textarea"
              value={text}
              spellCheck={false}
              placeholder='{ "format": "chem-bank-import", "version": 1, … }'
              onChange={(e) => {
                setText(e.target.value);
                setPhase('input');
                setParsed(null);
              }}
            />
          </div>

          <div className="row">
            <button className="btn btn--primary" disabled={!text.trim() || busy} onClick={() => void check()}>
              Check block
            </button>
            <button className="btn" onClick={() => fileInput.current?.click()} disabled={busy}>
              Choose .json files
            </button>
            <button className="btn" onClick={() => void loadBundled()} disabled={busy}>
              Load bundled sessions
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept="application/json,.json,.txt"
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                if (files.length > 0) void onPickFiles(files);
                e.target.value = '';
              }}
            />
          </div>
        </>
      ) : null}

      {parsed && parsed.errors.length > 0 ? (
        <Banner kind="error">
          <strong>
            {parsed.errors.length} problem{parsed.errors.length === 1 ? '' : 's'} — nothing was
            imported.
          </strong>
          <IssueList issues={parsed.errors} />
        </Banner>
      ) : null}

      {parsed && parsed.warnings.length > 0 ? (
        <Banner kind="warn">
          <strong>
            {parsed.warnings.length} warning{parsed.warnings.length === 1 ? '' : 's'} — these fields
            were ignored.
          </strong>
          <IssueList issues={parsed.warnings} limit={12} />
        </Banner>
      ) : null}

      {phase === 'preview' ? (
        <>
          <h2 className="section-title">Preview</h2>
          <ul className="list">
            {plans.map((plan, i) => (
              <li key={`${plan.sessionId}-${i}`} className="card">
                <div className="stack">
                  <strong>{describePlan(plan)}</strong>
                  <span className="faint">{describeCounts(plan)}</span>
                  {plan.blocker ? <Banner kind="error">{plan.blocker}</Banner> : null}
                  {plan.action === 'conflict' && plan.diff ? (
                    <Banner kind="warn">
                      Session <code>{plan.sessionId}</code> already exists as &ldquo;
                      {plan.existingTitle}&rdquo;. {plan.diff.added} new, {plan.diff.edited} edited,{' '}
                      {plan.diff.removed} removed. You&rsquo;ll choose what to do next.
                    </Banner>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          <div className="row">
            <button
              className="btn btn--primary"
              disabled={busy || plans.length === blockers.length}
              onClick={() => void confirm()}
            >
              {busy ? 'Importing…' : 'Import'}
            </button>
            <button className="btn" onClick={reset} disabled={busy}>
              Start over
            </button>
          </div>
        </>
      ) : null}

      {phase === 'done' ? (
        <>
          <Banner kind="ok">
            <strong>Imported.</strong>
            <ul className="issue-list">
              {applied.map((a, i) => (
                <li key={`${a.sessionId}-${i}`}>
                  <code>{a.sessionId}</code> — {a.decision}, {a.items} item
                  {a.items === 1 ? '' : 's'} written
                  {a.removed ? `, ${a.removed} removed` : ''}
                </li>
              ))}
            </ul>
          </Banner>
          <div className="row">
            <button className="btn btn--primary" onClick={() => navigate('/')}>
              Done
            </button>
            <button className="btn" onClick={reset}>
              Import another
            </button>
          </div>
        </>
      ) : null}

      {conflict ? (
        <Dialog title="This session already exists" onClose={() => setConflict(null)}>
          <p style={{ margin: 0 }}>
            <code>{conflict.plan.sessionId}</code> is already stored as &ldquo;
            {conflict.plan.existingTitle}&rdquo;.
          </p>
          {conflict.plan.diff ? (
            <p className="mono muted" style={{ margin: 0 }}>
              {conflict.plan.diff.added} new, {conflict.plan.diff.edited} edited,{' '}
              {conflict.plan.diff.removed} removed
            </p>
          ) : null}
          <div className="stack">
            <button className="btn btn--primary btn--block" disabled={busy} onClick={() => void resolveConflict('replace')}>
              Replace
            </button>
            <span className="faint">
              Use the new block. Your progress is kept for every id that still exists; anything no
              longer in the block is removed.
            </span>
            <button className="btn btn--block" disabled={busy} onClick={() => void resolveConflict('merge')}>
              Merge
            </button>
            <span className="faint">
              Add only the ids that are new. Everything already stored stays exactly as it is.
            </span>
            <button className="btn btn--block" disabled={busy} onClick={() => void resolveConflict('keep-both')}>
              Keep both
            </button>
            <span className="faint">
              Import as a separate session with a suffixed id, so nothing collides.
            </span>
            <button className="btn btn--ghost btn--block" disabled={busy} onClick={() => setConflict(null)}>
              Cancel
            </button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

function describePlan(plan: ImportPlan): string {
  const session = plan.block.session as { title?: string };
  const name = session.title ?? plan.sessionId;
  if (!plan.block.isFirstPart) {
    return `${name} — part ${plan.block.part.index} of ${plan.block.part.of}`;
  }
  if (plan.block.part.of > 1) {
    return `${name} — part ${plan.block.part.index} of ${plan.block.part.of}`;
  }
  return name;
}

function describeCounts(plan: ImportPlan): string {
  const c = plan.counts;
  const bits = [
    `${c.flashcards} card${c.flashcards === 1 ? '' : 's'}`,
    `${c.questions} question${c.questions === 1 ? '' : 's'}`,
  ];
  if (c.templates) bits.push(`${c.templates} problem template${c.templates === 1 ? '' : 's'}`);
  if (c.formulas) bits.push(`${c.formulas} formula${c.formulas === 1 ? '' : 's'}`);
  if (c.constants) bits.push(`${c.constants} constant${c.constants === 1 ? '' : 's'}`);
  return bits.join(', ');
}
